/**
 * Revision history and diff arithmetic (M4 Part A).
 *
 * History is append-only: a restore writes a *new* revision rather than
 * rewinding, so restoring is itself undoable. That is only legible if the row
 * says where it came from, which is what `describeRevision` is for.
 */

import type { BlogRevisionSummary } from "./types";

export interface RevisionDisplay {
  version: number;
  /** `v15 — restored from v09` (Q26), or plain `v15`. */
  label: string;
  originLabel: string;
  author: string;
  createdAt: string;
  /** The revision readers are being served. */
  isPublished: boolean;
  /** The newest revision. */
  isCurrent: boolean;
}

const ORIGIN_LABELS: Record<string, string> = {
  admin: "Edited here",
  mcp: "Authoring agent",
  ci: "Automated",
  restored: "Restored",
};

/** Zero-pad so `v09` and `v15` line up in a column. */
export function versionLabel(version: number): string {
  return `v${version < 10 ? "0" : ""}${String(version)}`;
}

export function describeRevision(
  revision: BlogRevisionSummary,
  context: { currentVersion: number; publishedVersion: number | null },
): RevisionDisplay {
  const restoredFrom = revision.restoredFromVersion;
  return {
    version: revision.version,
    label:
      restoredFrom === null
        ? versionLabel(revision.version)
        : `${versionLabel(revision.version)} — restored from ${versionLabel(restoredFrom)}`,
    originLabel: ORIGIN_LABELS[revision.origin] ?? revision.origin,
    author: revision.author,
    createdAt: revision.createdAt,
    isPublished: context.publishedVersion === revision.version,
    isCurrent: context.currentVersion === revision.version,
  };
}

export interface DiffPair {
  from: number | null;
  to: number;
}

/**
 * What the diff opens on: published → current (Q28).
 *
 * When they are equal there is nothing waiting to ship, and the panel says so
 * rather than rendering an empty two-column shell that looks like a bug.
 */
export function defaultDiffPair(context: {
  currentVersion: number;
  publishedVersion: number | null;
}): DiffPair {
  return { from: context.publishedVersion, to: context.currentVersion };
}

export type DiffState =
  | { kind: "nothing-waiting" }
  | { kind: "never-published"; to: number }
  | { kind: "first-revision" }
  | { kind: "compare"; from: number; to: number };

export function describeDiffState(pair: DiffPair, revisionCount: number): DiffState {
  if (revisionCount <= 1) return { kind: "first-revision" };
  if (pair.from === null) return { kind: "never-published", to: pair.to };
  if (pair.from === pair.to) return { kind: "nothing-waiting" };
  return { kind: "compare", from: pair.from, to: pair.to };
}

export function diffEmptyCopy(state: DiffState): string {
  switch (state.kind) {
    case "nothing-waiting":
      return "Nothing waiting to ship — the published revision and the current one are the same.";
    case "first-revision":
      return "This is the first revision. There is nothing to compare it against yet.";
    case "never-published":
      return `Nothing has ever been published, so ${versionLabel(state.to)} is compared against an empty document.`;
    default:
      return "";
  }
}

export type FieldDiff = { path: string; label: string; before: string; after: string };

/**
 * A shallow field-by-field comparison of two copies.
 *
 * Deliberately not a text diff: the reviewer is checking whether the right
 * fields moved, and a word-level diff across every language is a different
 * feature with a different budget.
 */
export function diffFields(
  before: readonly { path: string; label: string; value: string }[],
  after: readonly { path: string; label: string; value: string }[],
): FieldDiff[] {
  const beforeByPath = new Map(before.map((field) => [field.path, field.value]));
  const seen = new Set<string>();
  const diffs: FieldDiff[] = [];

  for (const field of after) {
    seen.add(field.path);
    const previous = beforeByPath.get(field.path) ?? "";
    if (previous !== field.value) {
      diffs.push({ path: field.path, label: field.label, before: previous, after: field.value });
    }
  }
  for (const field of before) {
    if (!seen.has(field.path)) {
      diffs.push({ path: field.path, label: field.label, before: field.value, after: "" });
    }
  }
  return diffs;
}

/**
 * The banner shown after a restore (Q18).
 *
 * Publishing every locale is neither cheap nor reversible, and restoring is
 * both — so the banner never makes Publish the primary action, and the caller
 * puts it behind its own confirm.
 */
export function restoreBannerCopy(args: {
  restoredVersion: number;
  sourceVersion: number;
}): string {
  return `${versionLabel(args.restoredVersion)} restores ${versionLabel(
    args.sourceVersion,
  )} as a new revision. Readers still see the published version — nothing has shipped.`;
}

export function restoreConfirmCopy(args: {
  sourceVersion: number;
  currentVersion: number;
}): string {
  return `Restoring ${versionLabel(args.sourceVersion)} writes it as ${versionLabel(
    args.currentVersion + 1,
  )}. Nothing is deleted: ${versionLabel(args.currentVersion)} stays in the history and this restore is itself undoable.`;
}
